import { startTransition, useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  apiClient,
  type IncidentExportRecord,
  type OperatorOrganizationRecord,
  type PartnerDeliveryAttemptRecord,
  type PartnerDeliveryCallbackEventRecord,
  type PartnerDeliveryRecord,
  type PartnerIntegrationRecord,
} from "@/lib/api/client"
import {
  formatDateTime,
  formatMaybeDate,
  formatRelativeTime,
  getPartnerSummary,
  INPUT_CLASS,
  matchesFilterText,
  safeJsonStringify,
  shortId,
  TEXTAREA_CLASS,
  titleizeToken,
} from "@/features/operator/advanced/advanced-ops-utils"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  canManagePortalOrganizations,
  resolveOperatorSession,
  type OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"

export interface OpsPartnerDeliveriesSearch {
  deliveryId?: string
  deliveryStatus?: "active" | "all" | "dead_letter" | "delivered" | "not_started" | "retryable"
  exportId?: string
  exportType?: string
  organizationId?: string
  q?: string
}

interface OpsPartnerDeliveriesWorkspaceProps {
  onSearchChange: (next: Partial<OpsPartnerDeliveriesSearch>) => void
  search: OpsPartnerDeliveriesSearch
}

interface PartnerDeliveriesBundle {
  deliveries: PartnerDeliveryRecord[]
  exports: IncidentExportRecord[]
  integrations: PartnerIntegrationRecord[]
  organizations: OperatorOrganizationRecord[]
  session: OperatorResolvedSession
}

interface ExportWorkspaceState {
  deliveries: PartnerDeliveryRecord[]
  errorMessage?: string | null
  exportRecord: IncidentExportRecord
  isLoading: boolean
}

const DISPATCHABLE_STATUSES = new Set(["queued", "retryable", "scheduled"])

function canAccessPartnerDeliveriesWorkspace(session: OperatorResolvedSession) {
  return Boolean(session.permissions.manageExports || session.permissions.viewAnalytics)
}

async function loadPartnerDeliveriesBundle(
  search: OpsPartnerDeliveriesSearch
): Promise<PartnerDeliveriesBundle> {
  const session = await resolveOperatorSession()

  if (!canAccessPartnerDeliveriesWorkspace(session)) {
    throw new Error("This session does not have access to partner deliveries.")
  }

  const organizationId = canManagePortalOrganizations(session)
    ? search.organizationId
    : undefined

  const [exportsResult, integrationsResult, deliveriesResult, organizationsResult] =
    await Promise.all([
      apiClient.getExportPackages({
        includeManifest: true,
        limit: 200,
        organizationId,
      }),
      apiClient.getPartnerIntegrations({
        organizationId,
      }),
      apiClient.getPartnerDeliveries({
        limit: 200,
        organizationId,
      }),
      canManagePortalOrganizations(session)
        ? apiClient.getOrganizations()
        : Promise.resolve(null),
    ])

  return {
    deliveries: deliveriesResult.data ?? [],
    exports: exportsResult.data ?? [],
    integrations: integrationsResult.data ?? [],
    organizations: organizationsResult?.data ?? [],
    session,
  }
}

function matchDeliveryStatus(
  summary: ReturnType<typeof getPartnerSummary>,
  filter: OpsPartnerDeliveriesSearch["deliveryStatus"]
) {
  if (!filter || filter === "all") {
    return true
  }

  if (filter === "not_started") {
    return (summary.totalDeliveries ?? 0) === 0
  }

  if (filter === "active") {
    return [
      "prepared",
      "scheduled",
      "queued",
      "processing",
      "submitted",
      "acknowledged",
    ].includes(summary.latestStatus ?? "")
  }

  if (filter === "retryable") {
    return summary.latestStatus === "retryable" || (summary.pendingRetryCount ?? 0) > 0
  }

  if (filter === "dead_letter") {
    return summary.latestStatus === "dead_letter" || (summary.deadLetterCount ?? 0) > 0
  }

  return summary.latestStatus === filter
}

function readJsonField(rawValue: FormDataEntryValue | null, fieldName: string) {
  const text = String(rawValue ?? "").trim()
  if (!text) {
    return undefined
  }

  try {
    return JSON.parse(text) as Record<string, unknown>
  } catch {
    throw new Error(`${fieldName} must contain valid JSON.`)
  }
}

function getSelectedExport(
  exportsList: IncidentExportRecord[],
  exportId?: string
) {
  if (exportId) {
    const direct = exportsList.find((record) => record.id === exportId)
    if (direct) {
      return direct
    }
  }

  return exportsList[0] ?? null
}

function getCompatibleIntegrations(
  exportRecord: IncidentExportRecord | null,
  integrations: PartnerIntegrationRecord[]
) {
  if (!exportRecord) {
    return []
  }

  return integrations.filter((integration) => {
    if (integration.status === "disabled") {
      return false
    }

    if (
      integration.organizationId
      && exportRecord.organizationId
      && integration.organizationId !== exportRecord.organizationId
    ) {
      return false
    }

    return true
  })
}

export function OpsPartnerDeliveriesWorkspace({
  onSearchChange,
  search,
}: OpsPartnerDeliveriesWorkspaceProps) {
  const [bundle, setBundle] = useState<PartnerDeliveriesBundle | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [workspaceByExportId, setWorkspaceByExportId] = useState<
    Record<string, ExportWorkspaceState>
  >({})
  const [callbackEventsByDeliveryId, setCallbackEventsByDeliveryId] = useState<
    Record<string, PartnerDeliveryCallbackEventRecord[]>
  >({})
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<string | null>(null)

  useEffect(() => {
    let isActive = true

    async function load() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const nextBundle = await loadPartnerDeliveriesBundle(search)

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(nextBundle)
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the partner deliveries workspace."
          )
          setIsLoading(false)
        })
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [search.organizationId])

  const filteredExports = useMemo(() => {
    if (!bundle) {
      return []
    }

    return bundle.exports.filter((record) => {
      if (search.exportType && search.exportType !== "all" && record.exportType !== search.exportType) {
        return false
      }

      const summary = getPartnerSummary(record)
      if (!matchDeliveryStatus(summary, search.deliveryStatus)) {
        return false
      }

      return matchesFilterText(search.q, [
        record.id,
        record.exportType,
        record.recipientKey,
        record.governance?.purposeLabel,
        record.governance?.regionKey,
        record.governance?.entitlementKey,
      ])
    })
  }, [bundle, search.deliveryStatus, search.exportType, search.q])

  const selectedExport = useMemo(
    () => getSelectedExport(filteredExports, search.exportId),
    [filteredExports, search.exportId]
  )
  const selectedExportId = selectedExport?.id

  useEffect(() => {
    if (!selectedExportId || workspaceByExportId[selectedExportId]) {
      return
    }

    let isActive = true

    setWorkspaceByExportId((previous) => ({
      ...previous,
      [selectedExportId]: {
        deliveries: [],
        exportRecord: selectedExport!,
        isLoading: true,
      },
    }))

    void apiClient
      .getExportDeliveryWorkspace(selectedExportId)
      .then((response) => {
        if (!isActive) {
          return
        }

        setWorkspaceByExportId((previous) => ({
          ...previous,
          [selectedExportId]: {
            deliveries: response.data ?? [],
            exportRecord: response.export,
            isLoading: false,
          },
        }))
      })
      .catch((error) => {
        if (!isActive) {
          return
        }

        setWorkspaceByExportId((previous) => ({
          ...previous,
          [selectedExportId]: {
            deliveries: [],
            errorMessage:
              error instanceof Error
                ? error.message
                : "Failed to load export delivery attempts.",
            exportRecord: selectedExport!,
            isLoading: false,
          },
        }))
      })

    return () => {
      isActive = false
    }
  }, [selectedExport, selectedExportId, workspaceByExportId])

  const selectedWorkspace = selectedExportId ? workspaceByExportId[selectedExportId] : null
  const selectedWorkspaceDeliveries = selectedWorkspace?.deliveries ?? []
  const selectedDelivery =
    selectedWorkspaceDeliveries.find((delivery) => delivery.id === search.deliveryId)
    ?? selectedWorkspaceDeliveries[0]
    ?? null
  const latestAttempt =
    (selectedDelivery?.attempts ?? []).slice().sort((left, right) => {
      return (right.attemptNumber ?? 0) - (left.attemptNumber ?? 0)
    })[0] ?? null
  const compatibleIntegrations = useMemo(
    () => getCompatibleIntegrations(selectedWorkspace?.exportRecord ?? selectedExport, bundle?.integrations ?? []),
    [bundle?.integrations, selectedExport, selectedWorkspace?.exportRecord]
  )
  const canWrite = Boolean(bundle?.session.permissions.manageExports)
  const portalScoped = Boolean(bundle && canManagePortalOrganizations(bundle.session))

  useEffect(() => {
    if (!selectedDelivery?.id || callbackEventsByDeliveryId[selectedDelivery.id]) {
      return
    }

    let isActive = true

    void apiClient
      .getPartnerDeliveryCallbackEvents(selectedDelivery.id, 30)
      .then((response) => {
        if (!isActive) {
          return
        }

        setCallbackEventsByDeliveryId((previous) => ({
          ...previous,
          [selectedDelivery.id]: response.data ?? [],
        }))
      })
      .catch(() => {
        if (!isActive) {
          return
        }

        setCallbackEventsByDeliveryId((previous) => ({
          ...previous,
          [selectedDelivery.id]: [],
        }))
      })

    return () => {
      isActive = false
    }
  }, [callbackEventsByDeliveryId, selectedDelivery?.id])

  async function refreshSelectedWorkspace(targetExportId = selectedExportId) {
    if (!targetExportId) {
      return
    }

    const exportRecord =
      bundle?.exports.find((record) => record.id === targetExportId) ?? selectedExport

    if (!exportRecord) {
      return
    }

    setWorkspaceByExportId((previous) => ({
      ...previous,
      [targetExportId]: {
        deliveries: previous[targetExportId]?.deliveries ?? [],
        exportRecord,
        isLoading: true,
      },
    }))

    const response = await apiClient.getExportDeliveryWorkspace(targetExportId)

    setWorkspaceByExportId((previous) => ({
      ...previous,
      [targetExportId]: {
        deliveries: response.data ?? [],
        exportRecord: response.export,
        isLoading: false,
      },
    }))
  }

  async function handleCreateDelivery(formData: FormData) {
    if (!selectedExportId) {
      return
    }

    setActionError(null)
    setPendingAction(`create:${selectedExportId}`)

    try {
      await apiClient.createPartnerDelivery(selectedExportId, {
        integrationId: String(formData.get("integrationId") ?? "").trim(),
        liveMode: String(formData.get("liveMode") ?? "").trim() || undefined,
        scheduleAt: String(formData.get("scheduleAt") ?? "").trim() || undefined,
        maxAttempts: String(formData.get("maxAttempts") ?? "").trim()
          ? Number(formData.get("maxAttempts"))
          : undefined,
        idempotencyKey:
          String(formData.get("idempotencyKey") ?? "").trim() || undefined,
        metadata: readJsonField(formData.get("metadata"), "Execution metadata"),
        requestPayload: readJsonField(formData.get("requestPayload"), "Request payload"),
      })
      await refreshSelectedWorkspace(selectedExportId)
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to create a partner delivery."
      )
    } finally {
      setPendingAction(null)
    }
  }

  async function handleRetryDelivery(formData: FormData) {
    if (!selectedDelivery) {
      return
    }

    setActionError(null)
    setPendingAction(`retry:${selectedDelivery.id}`)

    try {
      await apiClient.retryPartnerDelivery(selectedDelivery.id, {
        liveMode: String(formData.get("liveMode") ?? "").trim() || undefined,
        scheduleAt: String(formData.get("scheduleAt") ?? "").trim() || undefined,
        idempotencyKey:
          String(formData.get("idempotencyKey") ?? "").trim() || undefined,
        metadata: readJsonField(formData.get("metadata"), "Retry metadata"),
      })
      await refreshSelectedWorkspace(selectedExportId)
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to queue a retry attempt."
      )
    } finally {
      setPendingAction(null)
    }
  }

  async function handleReplayDelivery(formData: FormData) {
    if (!selectedDelivery) {
      return
    }

    setActionError(null)
    setPendingAction(`replay:${selectedDelivery.id}`)

    try {
      await apiClient.replayPartnerDelivery(selectedDelivery.id, {
        liveMode: String(formData.get("liveMode") ?? "").trim() || undefined,
        scheduleAt: String(formData.get("scheduleAt") ?? "").trim() || undefined,
        idempotencyKey:
          String(formData.get("idempotencyKey") ?? "").trim() || undefined,
        metadata: readJsonField(formData.get("metadata"), "Replay metadata"),
      })
      await refreshSelectedWorkspace(selectedExportId)
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to replay the delivery chain."
      )
    } finally {
      setPendingAction(null)
    }
  }

  async function handleDispatchLatestAttempt() {
    if (!latestAttempt) {
      return
    }

    setActionError(null)
    setPendingAction(`dispatch:${latestAttempt.id}`)

    try {
      await apiClient.dispatchPartnerDeliveryAttempt(latestAttempt.id)
      await refreshSelectedWorkspace(selectedExportId)
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to dispatch the latest attempt."
      )
    } finally {
      setPendingAction(null)
    }
  }

  return (
    <>
      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}
      {actionError ? (
        <OperatorStatusMessage tone="warning">{actionError}</OperatorStatusMessage>
      ) : null}

      {isLoading ? <PartnerDeliveriesLoadingState /> : null}

      {!isLoading && bundle ? (
        <>
          <OperatorPanel
            title="Filters and scope"
            description="Filter deliveries by organization, export type, status, and search text."
          >
            <div className="grid gap-4 lg:grid-cols-4">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Organization scope</span>
                <select
                  aria-label="Organization scope"
                  className={INPUT_CLASS}
                  disabled={!portalScoped}
                  value={search.organizationId ?? ""}
                  onChange={(event) =>
                    onSearchChange({
                      organizationId: event.target.value || undefined,
                    })
                  }
                >
                  <option value="">All visible organizations</option>
                  {bundle.organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Export type</span>
                <select
                  aria-label="Export type"
                  className={INPUT_CLASS}
                  value={search.exportType ?? "all"}
                  onChange={(event) =>
                    onSearchChange({
                      exportType:
                        event.target.value === "all" ? undefined : event.target.value,
                    })
                  }
                >
                  <option value="all">All export types</option>
                  <option value="evidence">Evidence</option>
                  <option value="insurance_fnol">Insurance FNOL</option>
                  <option value="government_share">Government share</option>
                  <option value="institutional_summary">Institutional summary</option>
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Delivery posture</span>
                <select
                  aria-label="Delivery posture"
                  className={INPUT_CLASS}
                  value={search.deliveryStatus ?? "all"}
                  onChange={(event) =>
                    onSearchChange({
                      deliveryStatus:
                        event.target.value === "all"
                          ? undefined
                          : (event.target.value as OpsPartnerDeliveriesSearch["deliveryStatus"]),
                    })
                  }
                >
                  <option value="all">All postures</option>
                  <option value="active">Active</option>
                  <option value="delivered">Delivered</option>
                  <option value="retryable">Retryable</option>
                  <option value="dead_letter">Dead letter</option>
                  <option value="not_started">No chains yet</option>
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Search</span>
                <input
                  aria-label="Search exports"
                  className={INPUT_CLASS}
                  value={search.q ?? ""}
                  placeholder="Export, recipient, governance, region"
                  onChange={(event) =>
                    onSearchChange({
                      q: event.target.value || undefined,
                    })
                  }
                />
              </label>
            </div>
          </OperatorPanel>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              detail={`${bundle.exports.filter((record) => record.exportType === "insurance_fnol").length} FNOL packages`}
              label="Exports in scope"
              value={filteredExports.length}
            />
            <OperatorMetricCard
              detail={`${bundle.deliveries.filter((delivery) => delivery.liveMode === "live").length} live delivery records`}
              label="Delivery records"
              value={bundle.deliveries.length}
            />
            <OperatorMetricCard
              detail={`${bundle.deliveries.filter((delivery) => delivery.status === "retryable").length} retryable`}
              label="Retry queue"
              value={
                bundle.deliveries.filter(
                  (delivery) =>
                    delivery.status === "retryable"
                    || Boolean(delivery.execution?.nextRetryAt)
                ).length
              }
            />
            <OperatorMetricCard
              detail={`${bundle.deliveries.filter((delivery) => delivery.status === "delivered").length} delivered`}
              label="Dead letter"
              value={
                bundle.deliveries.filter(
                  (delivery) =>
                    delivery.status === "dead_letter"
                    || Boolean(delivery.execution?.deadLetteredAt)
                ).length
              }
            />
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <OperatorPanel
              title="Export queue"
              description="Each row is an immutable manifest package. Queue, retry, and replay actions attach mutable chains without changing checksum or manifest content."
            >
              {filteredExports.length ? (
                <div className="grid gap-3">
                  {filteredExports.map((record) => {
                    const summary = getPartnerSummary(record)
                    const isActive = record.id === selectedExport?.id

                    return (
                      <button
                        key={record.id}
                        className={`rounded-3xl border px-4 py-4 text-left transition-colors ${
                          isActive
                            ? "border-primary/55 bg-primary/8"
                            : "border-border/70 bg-background/65 hover:border-primary/35"
                        }`}
                        type="button"
                        onClick={() =>
                          onSearchChange({
                            deliveryId: undefined,
                            exportId: record.id,
                          })
                        }
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold">
                              {titleizeToken(record.exportType)} · {shortId(record.id)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {record.recipientType || "recipient"} ·{" "}
                              {record.recipientKey || "unassigned"}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                              {titleizeToken(record.status)}
                            </span>
                            <span className="rounded-full bg-background px-3 py-1">
                              {titleizeToken(summary.latestStatus)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                          <div>Chains {summary.totalDeliveries ?? 0}</div>
                          <div>Latest activity {formatRelativeTime(summary.latestActivityAt)}</div>
                          <div>Purpose {record.governance?.purposeLabel || "n/a"}</div>
                          <div>Checksum {shortId(record.checksum, 12)}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No exports match the current filters."
                  message="Adjust the export type, delivery posture, or text search to inspect another manifest package."
                />
              )}
            </OperatorPanel>

            <OperatorPanel
              title="Live activity"
              description="This feed surfaces the most recent delivery activity across the filtered export queue."
            >
              {bundle.deliveries.length ? (
                <div className="grid gap-3">
                  {bundle.deliveries.slice(0, 10).map((delivery) => (
                    <button
                      key={delivery.id}
                      className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4 text-left"
                      type="button"
                      onClick={() =>
                        onSearchChange({
                          deliveryId: delivery.id,
                          exportId: delivery.exportId || undefined,
                        })
                      }
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm font-semibold">
                          {delivery.integration?.displayName || titleizeToken(delivery.partnerKey)}
                        </div>
                        <div className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
                          {titleizeToken(delivery.status)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground">
                        Export {shortId(delivery.exportId, 12)} · {titleizeToken(delivery.liveMode)} ·
                        callback {titleizeToken(delivery.execution?.callbackStatus || "waiting")}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No delivery activity in scope."
                  message="No partner delivery records are available in the current scope."
                />
              )}
            </OperatorPanel>
          </div>

          <PartnerDeliveryDetailPanel
            callbackEvents={selectedDelivery ? callbackEventsByDeliveryId[selectedDelivery.id] ?? [] : []}
            canWrite={canWrite}
            compatibleIntegrations={compatibleIntegrations}
            delivery={selectedDelivery}
            exportRecord={selectedWorkspace?.exportRecord ?? selectedExport}
            latestAttempt={latestAttempt}
            loading={Boolean(selectedWorkspace?.isLoading)}
            pendingAction={pendingAction}
            workspaceError={selectedWorkspace?.errorMessage}
            onCreateDelivery={handleCreateDelivery}
            onDispatchLatestAttempt={handleDispatchLatestAttempt}
            onReplayDelivery={handleReplayDelivery}
            onRetryDelivery={handleRetryDelivery}
            onSelectDelivery={(deliveryId) => onSearchChange({ deliveryId })}
            workspaceDeliveries={selectedWorkspaceDeliveries}
          />
        </>
      ) : null}
    </>
  )
}

function PartnerDeliveryDetailPanel({
  callbackEvents,
  canWrite,
  compatibleIntegrations,
  delivery,
  exportRecord,
  latestAttempt,
  loading,
  onCreateDelivery,
  onDispatchLatestAttempt,
  onReplayDelivery,
  onRetryDelivery,
  onSelectDelivery,
  pendingAction,
  workspaceDeliveries,
  workspaceError,
}: {
  callbackEvents: PartnerDeliveryCallbackEventRecord[]
  canWrite: boolean
  compatibleIntegrations: PartnerIntegrationRecord[]
  delivery: PartnerDeliveryRecord | null
  exportRecord: IncidentExportRecord | null
  latestAttempt: PartnerDeliveryAttemptRecord | null
  loading: boolean
  onCreateDelivery: (formData: FormData) => Promise<void>
  onDispatchLatestAttempt: () => Promise<void>
  onReplayDelivery: (formData: FormData) => Promise<void>
  onRetryDelivery: (formData: FormData) => Promise<void>
  onSelectDelivery: (deliveryId: string) => void
  pendingAction: string | null
  workspaceDeliveries: PartnerDeliveryRecord[]
  workspaceError?: string | null
}) {
  if (!exportRecord) {
    return (
      <OperatorPanel
        title="Selected export"
        description="Choose an export package to inspect its immutable manifest and delivery chains."
      >
        <OperatorEmptyState
          title="No export selected."
          message="The partner-delivery desk will show manifest details and mutable execution chains for the selected export."
        />
      </OperatorPanel>
    )
  }

  const dispatchDisabled =
    !canWrite
    || delivery?.liveMode !== "live"
    || !latestAttempt
    || !DISPATCHABLE_STATUSES.has(latestAttempt.status ?? "")

  return (
    <OperatorPanel
      title="Selected export"
      description="Manifest checksum stays immutable here. Queue, dispatch, retry, and replay mutate only the attached partner-delivery chain state."
    >
      {workspaceError ? (
        <OperatorStatusMessage tone="warning">{workspaceError}</OperatorStatusMessage>
      ) : null}
      {loading ? (
        <div className="grid gap-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="grid gap-3">
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">
                  {titleizeToken(exportRecord.exportType)} · {shortId(exportRecord.id)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {exportRecord.recipientType || "recipient"} ·{" "}
                  {exportRecord.recipientKey || "unassigned"}
                </div>
              </div>
              <div className="rounded-full bg-primary/10 px-3 py-1 text-xs text-primary">
                {titleizeToken(exportRecord.status)}
              </div>
            </div>
            <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <div>Checksum {exportRecord.checksum || "Pending"}</div>
              <div>Export version {exportRecord.contractVersion || "Unknown"}</div>
              <div>Purpose {exportRecord.governance?.purposeLabel || "n/a"}</div>
              <div>Region {exportRecord.governance?.regionKey || "n/a"}</div>
            </div>
            {exportRecord.manifest ? (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm font-medium">
                  Manifest preview
                </summary>
                <pre className="mt-2 max-h-72 overflow-auto rounded-2xl bg-background p-3 text-xs">
                  {safeJsonStringify(exportRecord.manifest)}
                </pre>
              </details>
            ) : null}
          </div>

          <form
            className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
            onSubmit={(event) => {
              event.preventDefault()
              void onCreateDelivery(new FormData(event.currentTarget))
            }}
          >
            <div className="text-sm font-semibold">Create delivery chain</div>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              Queue a new chain from this immutable export manifest.
            </p>
            <div className="mt-3 grid gap-3">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Partner integration</span>
                <select
                  aria-label="Partner integration"
                  className={INPUT_CLASS}
                  disabled={!canWrite}
                  name="integrationId"
                  required
                >
                  <option value="">Select integration</option>
                  {compatibleIntegrations.map((integration) => (
                    <option key={integration.id} value={integration.id}>
                      {integration.displayName || titleizeToken(integration.partnerKey)} ·{" "}
                      {titleizeToken(integration.deliveryMode)} /{" "}
                      {titleizeToken(integration.rolloutConfig?.mode)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Requested mode</span>
                  <select className={INPUT_CLASS} disabled={!canWrite} name="liveMode">
                    <option value="">Use integration default</option>
                    <option value="manual">Manual</option>
                    <option value="live">Live</option>
                  </select>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Schedule at</span>
                  <input className={INPUT_CLASS} disabled={!canWrite} name="scheduleAt" type="datetime-local" />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Max attempts</span>
                  <input
                    className={INPUT_CLASS}
                    disabled={!canWrite}
                    max={10}
                    min={1}
                    name="maxAttempts"
                    placeholder="Default"
                    type="number"
                  />
                </label>
              </div>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Idempotency key</span>
                <input className={INPUT_CLASS} disabled={!canWrite} name="idempotencyKey" />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Request payload JSON</span>
                <textarea className={TEXTAREA_CLASS} disabled={!canWrite} name="requestPayload" />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Execution metadata JSON</span>
                <textarea className={TEXTAREA_CLASS} disabled={!canWrite} name="metadata" />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button disabled={!canWrite || pendingAction === `create:${exportRecord.id}`} type="submit">
                {pendingAction === `create:${exportRecord.id}`
                  ? "Creating delivery chain…"
                  : "Create delivery chain"}
              </Button>
            </div>
          </form>
        </div>

        <div className="grid gap-3">
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="text-sm font-semibold">Execution chains</div>
            <div className="mt-3 grid gap-3">
              {workspaceDeliveries.length ? (
                workspaceDeliveries.map((item) => (
                  <button
                    key={item.id}
                    className={`rounded-2xl border px-3 py-3 text-left ${
                      item.id === delivery?.id
                        ? "border-primary/55 bg-primary/8"
                        : "border-border/70 bg-background/70"
                    }`}
                    type="button"
                    onClick={() => onSelectDelivery(item.id)}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium">
                        {item.integration?.displayName || titleizeToken(item.partnerKey)}
                      </span>
                      <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
                        {titleizeToken(item.status)}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      Delivery {shortId(item.id)} · attempt {item.currentAttemptNumber ?? 0}/
                      {item.maxAttempts ?? 0} · {titleizeToken(item.liveMode)}
                    </div>
                  </button>
                ))
              ) : (
                <OperatorEmptyState
                  title="No chains created yet."
                  message="Use the create form to queue the first delivery chain for this export."
                />
              )}
            </div>
          </div>

          {delivery ? (
            <>
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-semibold">
                    Selected delivery {shortId(delivery.id)}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                      {titleizeToken(delivery.status)}
                    </span>
                    <span className="rounded-full bg-background px-3 py-1">
                      {titleizeToken(delivery.liveMode)}
                    </span>
                  </div>
                </div>
                <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                  <div>Callback {titleizeToken(delivery.execution?.callbackStatus || "waiting")}</div>
                  <div>External delivery {delivery.execution?.externalDeliveryId || "Not assigned"}</div>
                  <div>Next retry {formatMaybeDate(delivery.execution?.nextRetryAt)}</div>
                  <div>Dead letter {formatMaybeDate(delivery.execution?.deadLetteredAt)}</div>
                </div>
              </div>

              <div className="grid gap-3 lg:grid-cols-3">
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  <div className="text-sm font-semibold">Dispatch latest attempt</div>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    Only queued, scheduled, or retryable attempts on live chains can dispatch.
                  </p>
                  <div className="mt-4">
                    <Button
                      disabled={
                        dispatchDisabled || pendingAction === `dispatch:${latestAttempt?.id ?? ""}`
                      }
                      onClick={() => void onDispatchLatestAttempt()}
                      type="button"
                    >
                      {pendingAction === `dispatch:${latestAttempt?.id ?? ""}`
                        ? "Dispatching…"
                        : "Dispatch latest attempt"}
                    </Button>
                  </div>
                </div>

                <form
                  className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                  onSubmit={(event) => {
                    event.preventDefault()
                    void onRetryDelivery(new FormData(event.currentTarget))
                  }}
                >
                  <div className="text-sm font-semibold">Retry chain</div>
                  <div className="mt-3 grid gap-3">
                    <select className={INPUT_CLASS} disabled={!canWrite} name="liveMode">
                      <option value="">Use current chain mode</option>
                      <option value="manual">Manual</option>
                      <option value="live">Live</option>
                    </select>
                    <input className={INPUT_CLASS} disabled={!canWrite} name="scheduleAt" type="datetime-local" />
                    <input className={INPUT_CLASS} disabled={!canWrite} name="idempotencyKey" placeholder="Idempotency key" />
                    <textarea className={TEXTAREA_CLASS} disabled={!canWrite} name="metadata" placeholder='{"operatorNote":"Retry after outage"}' />
                    <Button disabled={!canWrite || pendingAction === `retry:${delivery.id}`} type="submit">
                      {pendingAction === `retry:${delivery.id}` ? "Queueing retry…" : "Queue retry"}
                    </Button>
                  </div>
                </form>

                <form
                  className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                  onSubmit={(event) => {
                    event.preventDefault()
                    void onReplayDelivery(new FormData(event.currentTarget))
                  }}
                >
                  <div className="text-sm font-semibold">Replay as new chain</div>
                  <div className="mt-3 grid gap-3">
                    <select className={INPUT_CLASS} disabled={!canWrite} name="liveMode">
                      <option value="">Use current chain mode</option>
                      <option value="manual">Manual</option>
                      <option value="live">Live</option>
                    </select>
                    <input className={INPUT_CLASS} disabled={!canWrite} name="scheduleAt" type="datetime-local" />
                    <input className={INPUT_CLASS} disabled={!canWrite} name="idempotencyKey" placeholder="Idempotency key" />
                    <textarea className={TEXTAREA_CLASS} disabled={!canWrite} name="metadata" placeholder='{"operatorNote":"Replay after dead letter review"}' />
                    <Button disabled={!canWrite || pendingAction === `replay:${delivery.id}`} type="submit" variant="outline">
                      {pendingAction === `replay:${delivery.id}` ? "Creating replay…" : "Replay chain"}
                    </Button>
                  </div>
                </form>
              </div>

              <div className="grid gap-3 xl:grid-cols-2">
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  <div className="text-sm font-semibold">Attempt timeline</div>
                  <div className="mt-3 grid gap-3">
                    {(delivery.attempts ?? []).length ? (
                      delivery.attempts?.map((attempt) => (
                        <div key={attempt.id} className="rounded-2xl border border-border/70 px-3 py-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-medium">
                              Attempt {attempt.attemptNumber ?? 0}
                            </span>
                            <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
                              {titleizeToken(attempt.status)}
                            </span>
                          </div>
                          <div className="mt-2 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                            <div>Attempted {formatMaybeDate(attempt.execution?.attemptedAt)}</div>
                            <div>Response {attempt.execution?.responseCode ?? "n/a"}</div>
                            <div>Error {attempt.execution?.errorCode || "None"}</div>
                            <div>External {attempt.execution?.externalDeliveryId || "n/a"}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <OperatorEmptyState
                        title="No attempt records returned."
                        message="This delivery chain does not currently include attempt history."
                      />
                    )}
                  </div>
                </div>

                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  <div className="text-sm font-semibold">Callback events</div>
                  <div className="mt-3 grid gap-3">
                    {callbackEvents.length ? (
                      callbackEvents.map((event) => (
                        <div key={event.id} className="rounded-2xl border border-border/70 px-3 py-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-medium">
                              {titleizeToken(event.eventType)}
                            </span>
                            <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
                              {titleizeToken(event.callbackStatus || event.processingOutcome)}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-muted-foreground">
                            {formatDateTime(event.receivedAt)} · attempt {event.attemptId || "n/a"} ·
                            path {event.requestPath || "n/a"}
                          </div>
                          {event.payload ? (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-sm font-medium">
                                Payload snapshot
                              </summary>
                              <pre className="mt-2 max-h-56 overflow-auto rounded-2xl bg-background p-3 text-xs">
                                {safeJsonStringify(event.payload)}
                              </pre>
                            </details>
                          ) : null}
                        </div>
                      ))
                    ) : (
                      <OperatorEmptyState
                        title="No callback events recorded."
                        message="The current delivery chain has not received callback telemetry yet."
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </OperatorPanel>
  )
}

function PartnerDeliveriesLoadingState() {
  return (
    <>
      <OperatorPanel
        title="Filters and scope"
        description="Filter deliveries by organization, export type, status, and search text."
      >
        <div className="grid gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={`partner-deliveries-filter-${index}`}
              className="h-11 w-full rounded-2xl"
            />
          ))}
        </div>
      </OperatorPanel>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`partner-deliveries-metric-${index}`}
            className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
          >
            <Skeleton className="h-4 w-28" />
            <Skeleton className="mt-3 h-8 w-20" />
            <Skeleton className="mt-3 h-4 w-full" />
          </div>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <OperatorPanel
          title="Export queue"
          description="Each row is an immutable manifest package. Queue, retry, and replay actions attach mutable chains without changing checksum or manifest content."
        >
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`partner-deliveries-export-${index}`}
                className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="mt-2 h-4 w-28" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, detailIndex) => (
                    <Skeleton
                      key={`partner-deliveries-export-detail-${index}-${detailIndex}`}
                      className="h-4 w-full"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </OperatorPanel>

        <OperatorPanel
          title="Live activity"
          description="This feed surfaces the most recent delivery activity across the filtered export queue."
        >
          <div className="grid gap-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`partner-deliveries-activity-${index}`}
                className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
                <Skeleton className="mt-3 h-4 w-full" />
              </div>
            ))}
          </div>
        </OperatorPanel>
      </div>
    </>
  )
}
